import React from 'react';

import { Divider, Flex, Grid, GridItem, Box } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { useHistory } from 'react-router-dom';
import { checkUserIsEngagementAdmin } from 'utils/helpers/rolesAccess';

import { EngagementIcon, Play } from 'assets/assetsV1/iconComponents';
import engagementNoDataImageV1 from 'assets/assetsV1/illustrations/newIllustrations/engagementNoDataImageV1.svg';

import { getPulseSurveyRoute, getModuleSettingsRoute } from 'constants/helper';
import { routes } from 'constants/routes';

import { Button } from 'uiToolkitV1/Button';
import { DateComp } from 'uiToolkitV1/DateComp';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { EmptySummaryCard } from '../../EmptyCard/EmptySummaryCard';
import { MySummaryCardTemplate } from '../MySummaryCardTemplate';
import { useMySummaryEngagement } from './hooks';

const Container = styled(Flex)`
  padding: 12px 8px;
  &:hover {
    background: ${(props) => props.theme.colors.neutral.F6FAFE};
    & .highlightTextOnHover {
      color: #1565bc;
    }
  }
  cursor: pointer;
`;

const ButtonContainer = styled(Flex)`
  margin-left: auto;
  visibility: hidden;
  display: none;
  ${Container}:hover & {
    visibility: visible;
    display: flex;
  }
`;
const ViewDetailsButton = styled(Button)`
  margin-left: auto;
  visibility: hidden;
  display: none;
  ${Container}:hover & {
    visibility: visible;
    display: flex;
  }
`;

const ReviewsMyEngagementRow = ({ surveys, showDivider = true, restDrivers = [] }) => {
  const driverCharLimit = 15;
  const history = useHistory();
  const { name, deadline, surveyDrivers, id } = surveys;
  const totalDrivers = surveyDrivers?.length;
  const maxDriversToShow = 2;
  const showMoreCount = totalDrivers > maxDriversToShow ? totalDrivers - maxDriversToShow : 0;
  if (showMoreCount) {
    restDrivers = surveyDrivers?.slice(0, maxDriversToShow);
  }
  const driverName =
    showMoreCount === 0
      ? surveyDrivers.map((val) => (val.length > driverCharLimit ? `${val.substring(0, driverCharLimit)}...` : val))
      : `${restDrivers.map((val) =>
          val.length > driverCharLimit ? `${val.substring(0, driverCharLimit)}...` : val
        )}+${showMoreCount}`;

  const handleStartSurvey = ({ id }) => {
    const route = getPulseSurveyRoute(id);
    history.push(route);
  };

  return (
    <>
      <Container
        flexDir="column"
        onClick={() => {
          handleStartSurvey({ id });
        }}
      >
        <Grid width="100%" gap={2}>
          <GridItem colSpan={2}>
            <TextBodyMedium color="text.g1" className="highlightTextOnHover">
              {name}
            </TextBodyMedium>
            <Flex direction="row">
              <TextBodyMedium color="text.g2">
                <Flex>
                  <Box mr={1}> Drivers: </Box>
                  <Box mr={1}>{driverName}</Box>
                  <Box ml={1}>•</Box>
                </Flex>
              </TextBodyMedium>
              {deadline ? (
                <Flex>
                  <TextBodyMedium mx={1} color="text.g2">
                    Due:
                  </TextBodyMedium>
                  <DateComp date={deadline} isRelativeDateFormat />
                </Flex>
              ) : null}
            </Flex>
          </GridItem>
          <GridItem colStart={3} colEnd={7}>
            <ButtonContainer className="showOnHover">
              <ViewDetailsButton
                onClick={() => {
                  handleStartSurvey({ id });
                }}
                variant="rowButtonOutlined"
                StartIcon={Play}
              >
                Start
              </ViewDetailsButton>
            </ButtonContainer>
          </GridItem>
        </Grid>
      </Container>
      <Divider color="neutral.CFD7DF" />
    </>
  );
};

const EngagementMySummaryCard = ({ employeeId }) => {
  const { data, isLoading, isError } = useMySummaryEngagement({
    limit: 3,
    offset: 0,
  });
  if (isLoading) {
    return (
      <MySummaryCardTemplate showViewAllButton={false} Icon={EngagementIcon} heading="Engagement">
        <div style={{ marginTop: '16px' }}>
          <SkeletonLoader />
        </div>
      </MySummaryCardTemplate>
    );
  }
  if (isError) {
    return null;
  }

  if (!data?.surveys?.length) {
    return (
      <EmptySummaryCard
        Icon={EngagementIcon}
        heading="Engagement"
        description={
          checkUserIsEngagementAdmin()
            ? 'You have no surveys to complete right now. Start with setting up a new engagement survey for your organization.'
            : 'You have no engagement surveys to complete right now. We will notify you once your admin launches a new survey.'
        }
        illustrationSrc={engagementNoDataImageV1}
        redirectUrl={
          checkUserIsEngagementAdmin()
            ? getModuleSettingsRoute({
                module: 'engagement',
                activeTabId: 'engagement_surveys',
              })
            : null
        }
      />
    );
  }

  return (
    <MySummaryCardTemplate
      Icon={EngagementIcon}
      heading="Engagement"
      viewAllText="View All Surveys"
      viewAllHref={routes.engagement.index}
    >
      {data?.surveys?.map((surveys, index) => (
        <ReviewsMyEngagementRow surveys={surveys} showDivider={data?.data?.length - 1 !== index} />
      ))}
    </MySummaryCardTemplate>
  );
};

export { EngagementMySummaryCard };
